import * as React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Pause, Play} from 'lucide-react';
import {cn} from '../lib/cn';

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export interface AudioPlayerProps {
  src: string;
  compact?: boolean;
  className?: string;
}

export function AudioPlayer({src, compact, className}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => setDuration(audio.duration);

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    };

    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, [src]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }

    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleSeek = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      const bar = progressRef.current;
      if (!audio || !bar || !duration) return;

      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      audio.currentTime = ratio * duration;
    },
    [duration],
  );

  return (
    <div data-slot="audio-player" className={cn('flex items-center gap-2', className)}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        className={cn(
          'inline-flex items-center justify-center rounded-[var(--audio-player-button-radius)] bg-[var(--audio-player-button-bg)] hover:bg-[var(--audio-player-button-hover-bg)] [&_svg]:text-[var(--audio-player-icon-color)]',
          compact ? 'h-6 min-w-6 p-1' : 'h-8 min-w-8 p-1.5',
        )}
        onClick={togglePlayPause}
      >
        {isPlaying ? (
          <Pause className={cn(compact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'fill-current')} />
        ) : (
          <Play className={cn(compact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'fill-current')} />
        )}
      </button>
      {!compact && (
        <>
          <div
            ref={progressRef}
            className="h-1 flex-1 cursor-pointer overflow-hidden rounded-full bg-[var(--audio-player-track-bg)]"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-[var(--audio-player-progress-bg)] transition-all duration-100 ease-linear"
              style={{width: `${progress}%`}}
            />
          </div>
          <span className="min-w-10 text-xs font-medium text-[var(--audio-player-time-color)]">
            {isPlaying || currentTime > 0
              ? formatTime(currentTime)
              : formatTime(duration)}
          </span>
        </>
      )}
    </div>
  );
}

AudioPlayer.displayName = 'AudioPlayer';
